import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjeto } from '@/contexts/projeto-hook';
import { useAuth } from '@/contexts/auth-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { apiService } from '@/services/api';
import { ProjetoEditarModal } from './ProjetoEditarModal';
import type { Projeto } from '@/contexts/projeto-context';
import { FolderOpen, Plus, Loader2, Pencil, ArrowUp, ArrowDown, Users, ArrowLeft, Wallet, BookOpen } from 'lucide-react';

const PROJETO_ORDEM_ADMIN_KEY = 'projetoOrdemAdmin';
const SEM_SQUAD = '__none__';
type ModeloDocumento = 'arcelor' | 'praxis' | 'ar' | '';
type TipoProjeto = 'padrao' | 'budget' | 'manual_operacao';

interface SquadInfo {
  id: string;
  nome: string;
  regiao?: string | null;
  descricao?: string | null;
}

function nomeModeloDocumento(clienteTipo?: string | null): string | null {
  if (!clienteTipo) return null;
  if (clienteTipo === 'praxis') return 'Praxis';
  if (clienteTipo === 'ar') return 'AR Blueprint';
  return 'Arcelor';
}

function destinoProjeto(p: Projeto, isAdmin = false): string {
  if (p.tipoProjeto === 'budget') return '/budget';
  // Histórico de manuais: o editor sozinho não diz qual documento abrir.
  // O módulo está restrito a admin; quem não é cai no dashboard em vez de
  // bater num "acesso negado" logo depois de escolher o projeto.
  if (p.tipoProjeto === 'manual_operacao') return isAdmin ? '/manuais' : '/dashboard';
  return '/dashboard';
}

/**
 * Escolha do projeto ativo.
 *
 * A tela NÃO redireciona sozinha quando já existe projeto selecionado: o item
 * "Projetos" do menu leva exatamente para cá, e um redirecionamento automático
 * fazia o clique parecer quebrado — saía do dashboard e voltava para ele antes
 * de a lista aparecer. Quem chega aqui chegou de propósito, para trocar de
 * projeto; sair da tela é escolher outro projeto ou usar o menu.
 */
export function ProjetoSelecao() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const { projeto, projetos, selecionarProjeto, carregarProjetos, criarProjeto, atualizarProjeto, loading } = useProjeto();
  const [squads, setSquads] = useState<SquadInfo[]>([]);
  const [squadSelecionadoId, setSquadSelecionadoId] = useState<string | null>(null);
  const [mostrarModalCriar, setMostrarModalCriar] = useState(false);
  const [projetoParaEditar, setProjetoParaEditar] = useState<Projeto | null>(null);
  const [novoProjetoNome, setNovoProjetoNome] = useState('');
  const [novoProjetoDescricao, setNovoProjetoDescricao] = useState('');
  const [novoProjetoClienteTipo, setNovoProjetoClienteTipo] = useState<ModeloDocumento>('arcelor');
  const [novoProjetoTipo, setNovoProjetoTipo] = useState<TipoProjeto>('padrao');
  const [criandoProjeto, setCriandoProjeto] = useState(false);
  const [ordemProjetosAdmin, setOrdemProjetosAdmin] = useState<string[]>([]);
  const requestedInitialProjectsRef = useRef(false);

  useEffect(() => {
    if (!isAdmin) return;
    const loadSquads = async () => {
      const res = await apiService.listarSquads({ ativo: true });
      if (res.success && res.data) {
        setSquads(Array.isArray(res.data) ? res.data : []);
      }
    };
    loadSquads();
  }, [isAdmin]);

  useEffect(() => {
    if (requestedInitialProjectsRef.current) return;
    if (loading) return;
    // A tela de seleção precisa refletir o estado real do servidor mesmo quando
    // existe cache local, senão projetos removidos/renomeados continuam visíveis.
    if (navigator.onLine) {
      requestedInitialProjectsRef.current = true;
      carregarProjetos();
    }
  }, [carregarProjetos, loading]);

  useEffect(() => {
    if (!isAdmin) return;

    let ordemIds: unknown = [];
    try {
      const ordemSalva = localStorage.getItem(PROJETO_ORDEM_ADMIN_KEY);
      ordemIds = ordemSalva ? JSON.parse(ordemSalva) : [];
    } catch {
      ordemIds = [];
    }

    if (!Array.isArray(ordemIds)) {
      setOrdemProjetosAdmin([]);
      return;
    }

    const idsValidos = new Set(projetos.map((item) => item.id));
    const ordemFiltrada = ordemIds.filter((id: string) => idsValidos.has(id));
    const idsFaltantes = projetos.map((item) => item.id).filter((id) => !ordemFiltrada.includes(id));
    const ordemFinal = [...ordemFiltrada, ...idsFaltantes];

    setOrdemProjetosAdmin(ordemFinal);
    localStorage.setItem(PROJETO_ORDEM_ADMIN_KEY, JSON.stringify(ordemFinal));
  }, [isAdmin, projetos]);

  // Existe projeto sem squad? (aparece num card próprio para o admin)
  const temProjetoSemSquad = useMemo(
    () => isAdmin && projetos.some((p) => !p.squadId),
    [isAdmin, projetos]
  );

  const contagemPorSquad = useMemo(() => {
    const mapa = new Map<string, number>();
    projetos.forEach((p) => {
      const chave = p.squadId ?? SEM_SQUAD;
      mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
    });
    return mapa;
  }, [projetos]);

  // Admin vê os projetos do squad escolhido; usuário comum vê direto os do próprio
  // squad (o backend já filtra a listagem)
  const projetosDoSquad = useMemo(() => {
    if (!isAdmin) return projetos;
    if (!squadSelecionadoId) return [];
    if (squadSelecionadoId === SEM_SQUAD) return projetos.filter((p) => !p.squadId);
    return projetos.filter((p) => p.squadId === squadSelecionadoId);
  }, [isAdmin, projetos, squadSelecionadoId]);

  const projetosOrdenados = useMemo(() => {
    if (!isAdmin || ordemProjetosAdmin.length === 0) {
      return projetosDoSquad;
    }

    const indexPorId = new Map(ordemProjetosAdmin.map((id, index) => [id, index]));
    return [...projetosDoSquad].sort((a, b) => {
      const indexA = indexPorId.get(a.id);
      const indexB = indexPorId.get(b.id);
      if (indexA === undefined && indexB === undefined) return 0;
      if (indexA === undefined) return 1;
      if (indexB === undefined) return -1;
      return indexA - indexB;
    });
  }, [isAdmin, ordemProjetosAdmin, projetosDoSquad]);

  const squadSelecionado = squads.find((sq) => sq.id === squadSelecionadoId) ?? null;
  const mostrandoSquads = isAdmin && !squadSelecionadoId;

  const handleSelecionarProjeto = (projetoSelecionado: Projeto) => {
    selecionarProjeto(projetoSelecionado);
    navigate(destinoProjeto(projetoSelecionado, isAdmin));
  };

  const moverProjeto = (projetoId: string, direcao: 'up' | 'down') => {
    if (!isAdmin) return;

    // Troca com o vizinho VISÍVEL (mesmo squad), refletindo na ordem global
    const visiveis = projetosOrdenados.map((item) => item.id);
    const indexVisivel = visiveis.indexOf(projetoId);
    const indexDestino = direcao === 'up' ? indexVisivel - 1 : indexVisivel + 1;
    if (indexVisivel < 0 || indexDestino < 0 || indexDestino >= visiveis.length) return;
    const outroId = visiveis[indexDestino];

    const listaBase = ordemProjetosAdmin.length > 0 ? [...ordemProjetosAdmin] : projetos.map((item) => item.id);
    const i = listaBase.indexOf(projetoId);
    const j = listaBase.indexOf(outroId);
    if (i < 0 || j < 0) return;

    [listaBase[i], listaBase[j]] = [listaBase[j], listaBase[i]];
    setOrdemProjetosAdmin(listaBase);
    localStorage.setItem(PROJETO_ORDEM_ADMIN_KEY, JSON.stringify(listaBase));
  };

  const resetModalCriar = () => {
    setNovoProjetoNome('');
    setNovoProjetoDescricao('');
    setNovoProjetoClienteTipo('arcelor');
    setNovoProjetoTipo('padrao');
  };

  const handleCriarProjeto = async () => {
    if (!novoProjetoNome.trim()) {
      return;
    }

    setCriandoProjeto(true);
    try {
      const squadIdNovo = squadSelecionadoId && squadSelecionadoId !== SEM_SQUAD ? squadSelecionadoId : null;
      const resultado = await criarProjeto(
        novoProjetoNome,
        novoProjetoDescricao || undefined,
        novoProjetoTipo === 'padrao' ? novoProjetoClienteTipo : '',
        {
          squadId: squadIdNovo,
          tipoProjeto: novoProjetoTipo,
          ...(novoProjetoTipo === 'budget' ? { budgetHabilitado: true } : {}),
        }
      );
      if (resultado.success && resultado.data) {
        resetModalCriar();
        setMostrarModalCriar(false);
        selecionarProjeto(resultado.data);
        navigate(destinoProjeto(resultado.data, isAdmin));
      }
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
    } finally {
      setCriandoProjeto(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-schmersal-gray-light dark:bg-schmersal-gray-darkest">
        <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
      </div>
    );
  }

  // ===== Etapa 1 (admin): escolher o squad =====
  if (mostrandoSquads) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-schmersal-gray-light dark:bg-schmersal-gray-darkest p-4">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-schmersal-gray-dark dark:text-white mb-2">
              {t('projetos.squads.titulo')}
            </h1>
            <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
              {t('projetos.squads.subtitulo')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {squads.map((sq) => (
              <button
                key={sq.id}
                type="button"
                onClick={() => setSquadSelecionadoId(sq.id)}
                className="p-6 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-md hover:shadow-sm transition-shadow border-2 border-transparent hover:border-schmersal-blue dark:hover:border-schmersal-yellow text-left"
              >
                <div className="flex items-start space-x-3">
                  <Users className="h-8 w-8 text-schmersal-blue dark:text-schmersal-yellow flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-schmersal-gray-dark dark:text-white mb-1 truncate">
                      {sq.nome}
                    </h3>
                    {sq.regiao && (
                      <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {sq.regiao}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                      {t('projetos.squads.contagemProjetos', { total: contagemPorSquad.get(sq.id) ?? 0 })}
                    </p>
                  </div>
                </div>
              </button>
            ))}

            {temProjetoSemSquad && (
              <button
                type="button"
                onClick={() => setSquadSelecionadoId(SEM_SQUAD)}
                className="p-6 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-md hover:shadow-sm transition-shadow border-2 border-dashed border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue dark:hover:border-schmersal-yellow text-left"
              >
                <div className="flex items-start space-x-3">
                  <FolderOpen className="h-8 w-8 text-schmersal-gray-medium dark:text-schmersal-gray-light flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-schmersal-gray-dark dark:text-white mb-1">
                      {t('projetos.squads.semSquad')}
                    </h3>
                    <p className="mt-2 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                      {t('projetos.squads.contagemProjetos', { total: contagemPorSquad.get(SEM_SQUAD) ?? 0 })}
                    </p>
                  </div>
                </div>
              </button>
            )}
          </div>

          {squads.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light mx-auto mb-4" />
              <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
                {t('projetos.squads.vazio')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== Etapa 2: escolher o projeto =====
  return (
    <div className="min-h-screen flex items-center justify-center bg-schmersal-gray-light dark:bg-schmersal-gray-darkest p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          {isAdmin && (
            <button
              type="button"
              onClick={() => setSquadSelecionadoId(null)}
              className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 text-sm rounded-lg text-schmersal-gray-dark dark:text-white bg-white dark:bg-schmersal-gray-darker shadow hover:shadow-md transition-shadow"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('projetos.lista.voltarSquads')}
            </button>
          )}
          <h1 className="text-3xl font-bold text-schmersal-gray-dark dark:text-white mb-2">
            {isAdmin
              ? t('projetos.lista.tituloSquad', {
                  squad:
                    squadSelecionadoId === SEM_SQUAD
                      ? t('projetos.squads.semSquad')
                      : squadSelecionado?.nome ?? '',
                })
              : t('projetos.lista.titulo')}
          </h1>
          <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {isAdmin ? t('projetos.lista.subtituloAdmin') : t('projetos.lista.subtitulo')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {projetosOrdenados.map((p, index) => (
            <div
              key={p.id}
              className={`relative p-6 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-md hover:shadow-sm transition-shadow border-2 group ${
                p.id === projeto?.id
                  ? 'border-schmersal-turquoise'
                  : 'border-transparent hover:border-schmersal-blue dark:hover:border-schmersal-yellow'
              }`}
            >
              <button
                type="button"
                onClick={() => handleSelecionarProjeto(p)}
                className="w-full text-left"
              >
                <div className="flex items-start space-x-3">
                  {p.tipoProjeto === 'budget' ? (
                    <Wallet className="h-8 w-8 text-schmersal-blue dark:text-schmersal-yellow flex-shrink-0 mt-1" />
                  ) : p.tipoProjeto === 'manual_operacao' ? (
                    <BookOpen className="h-8 w-8 text-schmersal-blue dark:text-schmersal-yellow flex-shrink-0 mt-1" />
                  ) : (
                    <FolderOpen className="h-8 w-8 text-schmersal-blue dark:text-schmersal-yellow flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-schmersal-gray-dark dark:text-white mb-1 truncate">
                      {p.nome}
                    </h3>
                    {p.descricao && (
                      <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light line-clamp-2">
                        {p.descricao}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.id === projeto?.id && (
                        <span className="inline-flex rounded-full bg-schmersal-turquoise/15 px-2 py-0.5 text-xs font-semibold text-schmersal-blue dark:text-schmersal-turquoise">
                          {t('projetos.card.badgeAtual')}
                        </span>
                      )}
                      {p.tipoProjeto === 'budget' ? (
                        <span className="inline-flex rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                          {t('projetos.card.badgeBudget')}
                        </span>
                      ) : p.tipoProjeto === 'manual_operacao' ? (
                        <span className="inline-flex rounded-full bg-cyan-500/10 px-2 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-300">
                          {t('projetos.card.badgeManual')}
                        </span>
                      ) : (
                        <>
                          {nomeModeloDocumento(p.clienteTipo) && (
                            <span className="inline-flex rounded-full bg-schmersal-blue/10 px-2 py-0.5 text-xs font-medium text-schmersal-blue dark:bg-schmersal-yellow/10 dark:text-schmersal-yellow">
                              {t('projetos.card.badgeModelo', { modelo: nomeModeloDocumento(p.clienteTipo) ?? '' })}
                            </span>
                          )}
                          {p.budgetHabilitado && (
                            <span className="inline-flex rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                              {t('projetos.card.badgeBudgetCurto')}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {p.tipoProjeto === 'padrao' && p.contadores && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        <span>{t('projetos.card.contadorApreciacoes', { total: p.contadores.apreciacoes })}</span>
                        <span>•</span>
                        <span>{t('projetos.card.contadorTemplates', { total: p.contadores.templates })}</span>
                        <span>•</span>
                        <span>{t('projetos.card.contadorAtivos', { total: p.contadores.ativos })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
              {isAdmin && (
                <div className="absolute top-3 right-3 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moverProjeto(p.id, 'up');
                    }}
                    disabled={index === 0}
                    className="p-2 rounded-lg text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-schmersal-yellow transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={t('projetos.card.moverCima')}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      moverProjeto(p.id, 'down');
                    }}
                    disabled={index === projetosOrdenados.length - 1}
                    className="p-2 rounded-lg text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-schmersal-yellow transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={t('projetos.card.moverBaixo')}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProjetoParaEditar(p);
                    }}
                    className="p-2 rounded-lg text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-schmersal-yellow transition-colors"
                    title={t('projetos.card.editar')}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}

          {isAdmin && (
            <button
              type="button"
              onClick={() => setMostrarModalCriar(true)}
              className="p-6 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-md hover:shadow-sm transition-shadow border-2 border-dashed border-schmersal-gray-light dark:border-schmersal-gray-dark hover:border-schmersal-blue dark:hover:border-schmersal-yellow flex items-center justify-center"
            >
              <div className="text-center">
                <Plus className="h-8 w-8 text-schmersal-blue dark:text-schmersal-yellow mx-auto mb-2" />
                <span className="text-sm font-medium text-schmersal-gray-dark dark:text-white">
                  {t('projetos.lista.criarNovo')}
                </span>
              </div>
            </button>
          )}
        </div>

        {projetosDoSquad.length === 0 && (
          <div className="text-center py-12">
            <FolderOpen className="h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light mx-auto mb-4" />
            <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
              {isAdmin ? t('projetos.lista.vazioAdmin') : t('projetos.lista.vazio')}
            </p>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setMostrarModalCriar(true)}
                className="px-6 py-3 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors"
              >
                {t('projetos.lista.criarPrimeiro')}
              </button>
            )}
          </div>
        )}
      </div>

      <ProjetoEditarModal
        projeto={projetoParaEditar}
        onFechar={() => setProjetoParaEditar(null)}
        onSalvar={atualizarProjeto}
      />

      {/* Modal Criar Projeto */}
      {mostrarModalCriar && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-schmersal-gray-dark dark:text-white mb-1">
              {t('projetos.criar.titulo')}
            </h2>
            {isAdmin && squadSelecionadoId && squadSelecionadoId !== SEM_SQUAD && (
              <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
                {t('projetos.criar.squadDestino', { squad: squadSelecionado?.nome ?? '' })}
              </p>
            )}
            <div className="space-y-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  {t('projetos.form.nome')}
                </label>
                <input
                  type="text"
                  value={novoProjetoNome}
                  onChange={(e) => setNovoProjetoNome(e.target.value)}
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                  placeholder={t('projetos.form.nomePlaceholder')}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && novoProjetoNome.trim()) {
                      handleCriarProjeto();
                    }
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  {t('projetos.form.descricao')}
                </label>
                <textarea
                  value={novoProjetoDescricao}
                  onChange={(e) => setNovoProjetoDescricao(e.target.value)}
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                  placeholder={t('projetos.form.descricaoPlaceholder')}
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                  {t('projetos.form.tipoProjeto')}
                </label>
                <select
                  value={novoProjetoTipo}
                  onChange={(e) => setNovoProjetoTipo(e.target.value as TipoProjeto)}
                  className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                >
                  <option value="padrao">{t('projetos.form.tipoPadrao')}</option>
                  <option value="budget">{t('projetos.form.tipoBudget')}</option>
                  <option value="manual_operacao">{t('projetos.form.tipoManual')}</option>
                </select>
                {novoProjetoTipo === 'budget' && (
                  <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {t('projetos.form.ajudaBudget')}
                  </p>
                )}
                {novoProjetoTipo === 'manual_operacao' && (
                  <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {t('projetos.form.ajudaManualCriar')}
                  </p>
                )}
              </div>
              {novoProjetoTipo === 'padrao' && (
                <div>
                  <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">
                    {t('projetos.form.modeloDocumento')}
                  </label>
                  <select
                    value={novoProjetoClienteTipo}
                    onChange={(e) => setNovoProjetoClienteTipo(e.target.value as ModeloDocumento)}
                    className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                  >
                    <option value="">{t('projetos.form.modeloNenhum')}</option>
                    <option value="arcelor">Arcelor</option>
                  </select>
                  {/* Praxis e AR Blueprint saíram da escolha por enquanto. Os
                      geradores continuam de pé e os projetos que já usam esses
                      modelos seguem funcionando — só não se cria projeto novo
                      com eles. Para reativar, devolva as <option> aqui e no
                      ProjetoEditarModal. */}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setMostrarModalCriar(false);
                  resetModalCriar();
                }}
                className="px-4 py-2 text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-blue rounded-lg transition-colors"
                disabled={criandoProjeto}
              >
                {t('comum.acao.cancelar')}
              </button>
              <button
                onClick={handleCriarProjeto}
                disabled={!novoProjetoNome.trim() || criandoProjeto}
                className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {criandoProjeto ? t('projetos.criar.criando') : t('comum.acao.criar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
